<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../../components/Table.svelte';
  import Button from '../../../components/Button.svelte';
  import Pagination from '../../../components/Pagination.svelte';
  import Dropdown from '../../../components/Dropdown.svelte';
  import DateRange from '../../../components/DateRange.svelte';
  import DonationRequestModal from './DonationRequestModal.svelte';
    import { AuthService } from '../../../lib/api/auth.service';
  import EmployeeDonationService, {
    type DonationRequest,
    type DonationRequestFilters,
    type Employer
  } from '../../../lib/api/employee/donation.service';
  import { MyEmployerService } from '../../../lib/api/employee/myEmployer.service';
  import {
    stageFromStatusName,
    STATUS_BUCKET_LABEL,
    STATUS_BUCKET_LIFECYCLE_STAGES,
    isApprovedValueStage,
    statusBucketBadgeHtmlFromRow,
    type DonationStatusBucket,
    type LifecycleStage,
  } from '../../../lib/donationLifecycle';

  const statusBuckets: DonationStatusBucket[] = ['requested', 'approved', 'rejected'];
  const statusOptions: Array<{ uuid: string; name: string }> = [
    { uuid: '', name: 'All Statuses' },
    ...statusBuckets.map((bucket) => ({ uuid: bucket, name: STATUS_BUCKET_LABEL[bucket] })),
  ];

  // User data - gets from AuthService
  let user: { uuid: string; full_name?: string } | null = null;
  let loading = false;
  let error: string | null = null;

  // State for donations data
  let donations: DonationRequest[] = [];
  let filteredDonations: any[] = [];
  let currentPage = 1;
  let totalPages = 1;
  let totalItems = 0;

  // Filter states
  let selectedStatusBucket: DonationStatusBucket | '' = '';
  let selectedEmployer = '';
  let startDate = '';
  let endDate = '';

  // Data options
  let employerOptions: Array<{ uuid: string; name: string }> = [];

  // Modal state
  let showDonationModal = false;

  // Load employers for dropdown
  async function loadEmployers() {
    if (!user) return;
    
    try {
      console.log('Loading employers for user:', user.uuid);
      const result = await MyEmployerService.getEmployers(user.uuid);
      console.log('Employers result:', result);
      
      if (result.success && result.data) {
        const mappedEmployers = result.data.map((employer: Employer) => ({
          uuid: employer.uuid,
          name: employer.name
        }));
        
        employerOptions = [...mappedEmployers];
        console.log('Final employer options:', employerOptions);
      } else {
        console.error('Failed to load employers:', result.message);
      }
    } catch (err) {
      console.error('Error loading employers:', err);
    }
  }

  // Function to fetch donation requests
  async function fetchDonationRequests() {
    if (!user) return;
    
    loading = true;
    error = null;
    
    try {
      const requestedPage = currentPage;
      
      const filters: Omit<DonationRequestFilters, 'user_uuid'> = {
        page: requestedPage,
        per_page: 10,
        lifecycle_stage: selectedStatusBucket
          ? STATUS_BUCKET_LIFECYCLE_STAGES[selectedStatusBucket]
          : undefined,
        employer_uuid: selectedEmployer || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined
      };

      console.log('Fetching donation requests with filters:', filters);
      const response = await EmployeeDonationService.getDonationRequests(user.uuid, filters);
      console.log('API Response:', response);
      
      if (response && response.success) {
        donations = response.data || [];
        
        // Update pagination from response
        if (response.paging) {
          totalPages = response.paging.last_page || 1;
          totalItems = response.paging.total || donations.length;
          // Keep the requested page, don't override with server response
          // Only use server response if requestedPage is invalid
          if (requestedPage > (response.paging.last_page || 1)) {
            currentPage = 1; // Reset to page 1 if requested page is beyond available pages
          } else {
            currentPage = requestedPage; // Keep the page we requested
          }
        } else {
          totalPages = 1;
          totalItems = donations.length;
          currentPage = 1;
        }
        
        // Transform for UI display if we have data
        console.log("donations", donations);
        if (donations.length > 0) {
          filteredDonations = donations.map(donation => ({
            uuid: donation.uuid,
            id: donation.uuid,
            charity_name: donation.charity?.name || 'Unknown Charity',
            charity: donation.charity,
            leave_hours: donation.leave_hours,
            created_at: donation.created_at,
            employer_name: donation.employer?.name || 'Unknown Employer',
            employer: donation.employer,
            date: EmployeeDonationService.formatDate(donation.approved_at),
            status: donation.donation_request_status?.name || 'Pending',
            lifecycle_stage: donation.lifecycle_stage,
            payment_status: donation.payment_status || 'not_paid',
            employer_payment_date: donation.employer_payment_date,
            charity_payment_date: donation.charity_payment_date,
            employer_payment_reference: donation.employer_payment_reference,
            payout_uuid: donation.payout_uuid,
            // Include original data for status badge
            donation_request_status: donation.donation_request_status
          }));
        } else {
          filteredDonations = [];
          // Don't set error for empty results - it's a valid empty state
          error = null;
        }
      } else {
        donations = [];
        filteredDonations = [];
        error = response.message || 'Failed to load donation requests';
      }
    } catch (err) {
      console.error('Error fetching donation requests:', err);
      error = 'Failed to load donation requests. Please try again.';
      donations = [];
      filteredDonations = [];
    } finally {
      loading = false;
    }
  }

  // Filter functions
  function handleStatusChange(value: string) {
    selectedStatusBucket = (value || '') as DonationStatusBucket | '';
    currentPage = 1; // Reset to first page when filter changes
    fetchDonationRequests();
  }

  function handleEmployerChange(value: string) {
    selectedEmployer = value;
    currentPage = 1;
    fetchDonationRequests();
  }

  function handleDateChange() {
    currentPage = 1;
    fetchDonationRequests();
  }

  // Table columns
  const columns = [
    {
      key: 'charity_name',
      label: 'Charity Name',
      width: '30%', // Reduced to make room for payment status
      render: (value: string, row: any) => {
        const charityUuid = row.charity?.uuid || '';
        const charityName = value || 'Unknown Charity';
        if (charityUuid) {
          return `<div class="charity-info">
            <button class="charity-name-button" data-charity-uuid="${charityUuid}" type="button" aria-label="View charity details">
              <span class="charity-name">${charityName}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>`;
        } else {
          return `<div class="charity-info">
            <span class="charity-name">${charityName}</span>
          </div>`;
        }
      }
    },
    {
      key: 'employer_name',
      label: 'Employer Name',
      width: '20%', // Reduced
      render: (value: string) => `<span class="employer-name">${value || 'Unknown Employer'}</span>`
    },
    {
      key: 'leave_hours',
      label: 'Hours',
      width: '90px', // Fixed width
      render: (value: number) => `<span class="hours-value">${value} hours</span>`
    },
    {
      key: 'date',
      label: 'Date',
      width: '120px', // Fixed width
      render: (value: string) => `<span class="date-value">${value}</span>`
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (_value: string, row: any) => statusBucketBadgeHtmlFromRow(row)
    },
    {
      key: 'payment_status',
      label: 'Payment Status',
      width: '140px', // Fixed width
      render: (value: string, row: any) => {
        const paymentStatus = row.payment_status || 'not_paid';
        const statusClass = paymentStatus === 'completed' ? 'completed' : paymentStatus === 'paid' ? 'paid' : 'not_paid';
        const statusText = paymentStatus === 'completed' ? 'Completed' : paymentStatus === 'paid' ? 'Paid' : 'Not Paid';
        return `<div class="payment-status-badge payment-status-${statusClass}">${statusText}</div>`;
      }
    }
  ];

  // Wire up charity click handlers after table update (status badge is rendered inline).
  afterUpdate(() => {
    // Handle charity name clicks
    const charityButtons = document.querySelectorAll('.charity-name-button');
    charityButtons.forEach((button) => {
      const charityUuid = button.getAttribute('data-charity-uuid');
      if (charityUuid && !button.hasAttribute('data-click-handler')) {
        button.setAttribute('data-click-handler', 'true');
        button.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent row click
          push(`/view-charity/${charityUuid}`);
        });
        // Add keyboard accessibility
        button.addEventListener('keydown', (e: Event) => {
          const keyEvent = e as KeyboardEvent;
          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
            keyEvent.preventDefault();
            keyEvent.stopPropagation();
            push(`/view-charity/${charityUuid}`);
          }
        });
      }
    });
  });

  function handleRowClick(row: any) {
    if (row.uuid) {
      push(`/my-donation/${row.uuid}`);
    }
  }

  // Pagination functions
  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages) {
      currentPage = page;
      fetchDonationRequests();
    }
  }

  function handleNewDonationRequest() {
    showDonationModal = true;
  }

  function handleModalClose() {
    showDonationModal = false;
  }

  function handleDonationComplete() {
    fetchDonationRequests();
  }

  onMount(async () => {
    // Get user from AuthService
    user = AuthService.getUser();
    
    if (!user) {
      error = 'User data not found.';
      return;
    }

    // Load data
    await loadEmployers();

    // Fetch donation requests
    fetchDonationRequests();
  });
  </script>
  
<div class="donations-page">
  <!-- Header Section -->
  <div class="page-header">
    <h1 class="page-title">My Donation Requests</h1>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <Dropdown
          options={statusOptions}
          selectedValue={selectedStatusBucket}
          placeholder="All Statuses"
          onSelectionChange={handleStatusChange}
        />
      </div>
      
      <div class="filter-item">
        <Dropdown
          options={employerOptions}
          selectedValue={selectedEmployer}
          placeholder="All Employers"
          onSelectionChange={handleEmployerChange}
        />
      </div>
      
      <div class="filter-item">
        <DateRange
          bind:startDate
          bind:endDate
          placeholder="Date Range"
          on:change={handleDateChange}
        />
      </div>
    </div>
  </div>

  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading donation requests...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <div class="error-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={() => fetchDonationRequests()}>
        Try Again
      </Button>
    </div>
  {:else if filteredDonations.length === 0}
    <div class="empty-state">
      <div class="empty-icon">
        {#if selectedStatusBucket || selectedEmployer || startDate || endDate}
          <!-- Filtered empty state -->
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
        {:else}
          <!-- No donations at all -->
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        {/if}
      </div>
      {#if selectedStatusBucket || selectedEmployer || startDate || endDate}
        <h3>No donations match your filters</h3>
        <p>Try adjusting your filters to see more results</p>
      {:else}
        <h3>No donation requests yet</h3>
        <p>Start making a difference by creating your first donation request</p>
        <Button variant="primary" on:click={handleNewDonationRequest}>
          Create Donation Request
        </Button>
      {/if}
    </div>
  {:else}
    <div class="table-section">
      <Table 
        columns={columns}
        data={filteredDonations}
        onRowClick={handleRowClick}
      />
    </div>
    
    {#if totalPages > 1}
      <div class="pagination-container">
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          on:pageChange={(event) => goToPage(event.detail.page)} 
        />
      </div>
    {/if}
        {/if}
      </div>

<!-- Donation Request Modal -->
<DonationRequestModal 
  isOpen={showDonationModal}
  on:close={handleModalClose}
  on:complete={handleDonationComplete}
/>
  
  <style>
  .donations-page {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .page-header {
    background: white;
    padding: 1rem 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .page-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .filters-section {
      display: flex;
      justify-content: space-between;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 1rem 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .filters-left {
    display: flex;
      gap: 1rem;
    align-items: center;
    flex: 1;
  }

  .filter-item {
    position: relative;
    min-width: 180px;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  /* Optimize column widths - fixed columns won't grow */
  :global(.table-section .data-table th:nth-child(3)),
  :global(.table-section .data-table td:nth-child(3)) {
    /* Hours column */
    width: 100px;
    max-width: 100px;
    min-width: 100px;
  }

  :global(.table-section .data-table th:nth-child(4)),
  :global(.table-section .data-table td:nth-child(4)) {
    /* Date column */
    width: 140px;
    max-width: 140px;
    min-width: 140px;
  }

  :global(.table-section .data-table th:nth-child(5)),
  :global(.table-section .data-table td:nth-child(5)) {
    /* Status column */
    width: 120px;
    max-width: 120px;
    min-width: 120px;
    text-align: left;
    vertical-align: middle;
  }

  /* Flexible columns - Charity Name gets more space */
  :global(.table-section .data-table th:nth-child(1)),
  :global(.table-section .data-table td:nth-child(1)) {
    /* Charity Name column - larger share */
    min-width: 250px;
    width: 35%;
  }

  :global(.table-section .data-table th:nth-child(2)),
  :global(.table-section .data-table td:nth-child(2)) {
    /* Employer Name column - smaller share */
    min-width: 150px;
    width: 25%;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f59e0b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-icon {
    color: var(--color-negative, #dc2626);
    margin-bottom: 1rem;
  }

  .error-message {
    color: var(--color-negative, #dc2626);
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    min-height: 300px;
  }

  .empty-icon {
    color: #9ca3af;
    margin-bottom: 1.5rem;
    opacity: 0.6;
  }

  .empty-state h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 0.5rem;
    color: #111827;
  }

  .empty-state p {
    color: #6b7280;
    margin-bottom: 0;
    font-size: 0.875rem;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  /* Global styles for table content */
  :global(.charity-info) {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  :global(.charity-name-button) {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    padding: 0;
    font-weight: 500;
    color: #111827;
    font-size: inherit;
    font-family: inherit;
    cursor: pointer;
    transition: color 0.2s;
  }

  :global(.charity-name-button:hover) {
    color: var(--color-primary, #f0b429);
  }

  :global(.charity-name-button:focus) {
    outline: none;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
    border-radius: 4px;
  }

  :global(.charity-name-button svg) {
    flex-shrink: 0;
    opacity: 0.6;
    transition: opacity 0.2s, transform 0.2s;
  }

  :global(.charity-name-button:hover svg) {
    opacity: 1;
    transform: translateX(2px);
  }

  :global(.charity-name) {
    font-weight: 500;
    color: inherit;
  }

  :global(.hours-value) {
    font-weight: 500;
    color: #059669;
  }

  :global(.date-value) {
    color: #6b7280;
    font-size: 0.875rem;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .filters-section {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      gap: 0.75rem;
    }

    }
  </style> 